// RelayScreenWidget — renders inline in chat when Vella executes a relay command.
// data shape: { screenshot_url, command, status, duration_ms, url, error }
import React, { useState } from 'react';
import { TEXT, TEXT2, TEXT3, BLUE, SUCCESS, WARNING, DANGER, FONT } from '../../shared';

const STATUS_CFG = {
  pending:   { color: TEXT3,   label: 'Pending',    icon: null },
  executing: { color: BLUE,    label: 'Running',    icon: 'spin' },
  completed: { color: SUCCESS, label: 'Done',       icon: 'check' },
  failed:    { color: DANGER,  label: 'Failed',     icon: 'x' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 10, fontWeight: 700, color: cfg.color,
      background: `${cfg.color}14`, border: `1px solid ${cfg.color}30`,
      borderRadius: 999, padding: '2px 7px',
    }}>
      {cfg.icon === 'spin' && (
        <span style={{ width: 8, height: 8, border: `1.5px solid ${BLUE}40`, borderTopColor: BLUE, borderRadius: '50%', display: 'inline-block', animation: 'relayWidgetSpin 0.7s linear infinite' }} />
      )}
      {cfg.icon === 'check' && (
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={SUCCESS} strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
      )}
      {cfg.icon === 'x' && (
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={DANGER} strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      )}
      {cfg.label}
    </span>
  );
}

export default function RelayScreenWidget({ data, onAction }) {
  const [expanded, setExpanded] = useState(true);
  if (!data) return null;

  const hasScreenshot = !!data.screenshot_url;

  return (
    <>
      <style>{`
        @keyframes relayWidgetSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
      <div style={{
        background: 'rgba(255,255,255,0.55)',
        backdropFilter: 'blur(22px)',
        WebkitBackdropFilter: 'blur(22px)',
        border: '1px solid rgba(255,255,255,0.60)',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 8px 24px -10px rgba(11,107,211,0.14)',
        fontFamily: FONT,
      }}>
        {/* Header row */}
        <div
          onClick={() => hasScreenshot && setExpanded(e => !e)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 14px',
            borderBottom: expanded && hasScreenshot ? '1px solid rgba(180,205,235,0.30)' : 'none',
            cursor: hasScreenshot ? 'pointer' : 'default',
            background: 'rgba(255,255,255,0.45)',
          }}
        >
          {/* Monitor icon */}
          <div style={{
            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
            background: 'rgba(14,165,233,0.10)', border: '1px solid rgba(14,165,233,0.22)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round">
              <rect x="2" y="3" width="20" height="14" rx="2"/>
              <line x1="8" y1="21" x2="16" y2="21"/>
              <line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: TEXT, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {data.command || 'Relay command'}
            </div>
            {data.url && (
              <div style={{ fontSize: 11, color: TEXT3, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {data.url}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
            {data.duration_ms != null && (
              <span style={{ fontSize: 10, color: TEXT3 }}>
                {data.duration_ms < 1000 ? `${data.duration_ms}ms` : `${(data.duration_ms / 1000).toFixed(1)}s`}
              </span>
            )}
            <StatusBadge status={data.status || 'completed'} />
            {hasScreenshot && (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={TEXT3} strokeWidth="2"
                style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            )}
          </div>
        </div>

        {/* Screenshot */}
        {expanded && hasScreenshot && (
          <div style={{ position: 'relative' }}>
            <img
              src={data.screenshot_url}
              alt="Relay result"
              style={{ width: '100%', display: 'block', maxHeight: 300, objectFit: 'contain', background: '#E8EEF5' }}
            />
          </div>
        )}

        {/* Error message */}
        {data.error && (
          <div style={{ padding: '8px 14px', background: `${DANGER}08`, borderTop: `1px solid ${DANGER}20` }}>
            <span style={{ fontSize: 12, color: DANGER }}>{data.error}</span>
          </div>
        )}

        {/* Actions for completed commands */}
        {data.status === 'completed' && (
          <div style={{ padding: '8px 14px', display: 'flex', gap: 7, borderTop: '1px solid rgba(180,205,235,0.25)', background: 'rgba(255,255,255,0.38)' }}>
            <button
              onClick={() => onAction?.({ type: 'send_message', text: 'Take another screenshot and show me the current state of the screen.' })}
              style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid rgba(14,165,233,0.30)', background: 'rgba(14,165,233,0.08)', color: BLUE, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}
            >
              Refresh view
            </button>
            <button
              onClick={() => onAction?.({ type: 'send_message', text: 'What do you see on the screen right now?' })}
              style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid rgba(180,205,235,0.55)', background: 'rgba(255,255,255,0.55)', color: TEXT2, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: FONT }}
            >
              Describe screen
            </button>
          </div>
        )}
      </div>
    </>
  );
}
